import { TaskType } from '../../../enums/enums';
import { type UserTasksAndResponses } from '../../../models/userTasksAndResponses';
import { baseTasks } from '../fixtures/tasks';
import { nowIso } from '../support/date-utils';
import { getScenario } from '../support/scenarios';
import { readStorage, storageKeys, writeStorage } from '../support/storage';

// --- Helpers ---

/**
 * Calculates the count of pending tasks.
 */
function calculateTaskCount(tasks: UserTasksAndResponses): number {
  const taskEntries = [
    tasks.firstAssessment,
    tasks.midCohortGroupAssessment,
    tasks.finalGroupAssessment,
    tasks.pilotGroupAssessment,
    tasks.oneOnOneFinalAssessment,
    tasks.oneOnOneMidCohortAssessment
  ];

  const pendingTasks = taskEntries.filter((task) => task.exists && !task.submitted).length;

  // WHY: Terms and conditions are treated as a mandatory pending task until accepted
  return pendingTasks + (tasks.termsAndConditions.submitted ? 0 : 1);
}

/**
 * Resolves the task storage key based on the active persona.
 */
function getTaskStorageKey(): string {
  const persona = getScenario('persona', 'mentee');
  const keyMap: Record<string, string> = {
    coach: storageKeys.tasksCoach,
    leader: storageKeys.tasksLeader,
    new: storageKeys.tasksNew,
    veteran: storageKeys.tasksVeteran,
    mentee: storageKeys.tasks
  };
  return keyMap[persona] || storageKeys.tasks;
}

/**
 * Returns the persisted task state without any scenario overlays.
 */
function getStoredTasks(): UserTasksAndResponses {
  return readStorage(getTaskStorageKey(), baseTasks);
}

/**
 * Applies the "done" scenario without mutating the stored baseline tasks.
 */
function applyDoneScenario(tasks: UserTasksAndResponses): UserTasksAndResponses {
  return {
    ...tasks,
    tasksCount: 0,
    termsAndConditions: { submitted: true, submissionDate: nowIso() },
    firstAssessment: { ...tasks.firstAssessment, submitted: true },
    midCohortGroupAssessment: { ...tasks.midCohortGroupAssessment, submitted: true },
    finalGroupAssessment: { ...tasks.finalGroupAssessment, submitted: true },
    pilotGroupAssessment: { ...tasks.pilotGroupAssessment, submitted: true },
    oneOnOneFinalAssessment: { ...tasks.oneOnOneFinalAssessment, submitted: true },
    oneOnOneMidCohortAssessment: { ...tasks.oneOnOneMidCohortAssessment, submitted: true }
  };
}

// --- Public Domain API ---

/**
 * Returns mock tasks, potentially adjusted by scenario.
 */
export function getDevMockTasks(): UserTasksAndResponses {
  const scenario = getScenario('tasks', 'default');
  const tasks = getStoredTasks();

  if (scenario === 'done') {
    // WHY: Scenario overlays should remain ephemeral so writes still preserve the underlying task baseline.
    return applyDoneScenario(tasks);
  }

  return tasks;
}

/**
 * Updates mock tasks and recalculates count.
 */
function updateTasks(updates: Partial<UserTasksAndResponses>): UserTasksAndResponses {
  const nextTasks = {
    ...getStoredTasks(),
    ...updates
  };

  nextTasks.tasksCount = calculateTaskCount(nextTasks);
  return writeStorage(getTaskStorageKey(), nextTasks);
}

/**
 * Submits a mock task.
 */
export function submitDevMockTask(taskName: TaskType): UserTasksAndResponses {
  const submittedAt = nowIso();
  const currentTasks = getStoredTasks();

  switch (taskName) {
    case TaskType.FirstSessionAssessment:
      return updateTasks({
        firstAssessment: {
          ...currentTasks.firstAssessment,
          submitted: true,
          submissionDate: submittedAt
        }
      });
    case TaskType.MidCohortGroupAssessment:
      return updateTasks({
        midCohortGroupAssessment: {
          ...currentTasks.midCohortGroupAssessment,
          submitted: true,
          submissionDate: submittedAt
        }
      });
    case TaskType.FinalGroupAssessment:
      return updateTasks({
        finalGroupAssessment: {
          ...currentTasks.finalGroupAssessment,
          submitted: true,
          submissionDate: submittedAt
        }
      });
    case TaskType.PilotGroupCoachingAssessment:
      return updateTasks({
        pilotGroupAssessment: {
          ...currentTasks.pilotGroupAssessment,
          submitted: true,
          submissionDate: submittedAt
        }
      });
    case TaskType.OneOnOneFinalSurvey:
      return updateTasks({
        oneOnOneFinalAssessment: {
          ...currentTasks.oneOnOneFinalAssessment,
          submitted: true,
          submissionDate: submittedAt
        }
      });
    case TaskType.OneOnOneMidCohortSurvey:
      return updateTasks({
        oneOnOneMidCohortAssessment: {
          ...currentTasks.oneOnOneMidCohortAssessment,
          submitted: true,
          submissionDate: submittedAt
        }
      });
    default:
      return currentTasks;
  }
}

/**
 * Accepts mock terms and conditions.
 */
export function acceptDevMockTerms() {
  const currentTasks = getStoredTasks();
  return updateTasks({
    termsAndConditions: {
      ...currentTasks.termsAndConditions,
      submitted: true,
      submissionDate: nowIso()
    }
  });
}
